

import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EvolutionPlan } from '../../models/correction.model';

@Component({
  selector: 'app-evolution-plan',
  standalone: true,
  template: `


<div class="space-y-6 animate-fade-in-content">
  <!-- Header -->
  <div class="bg-white p-5 md:p-6 rounded-2xl shadow-card border border-slate-200/80">
    <div class="flex flex-col md:flex-row justify-between items-center gap-4">
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Seu Plano de Evolução Individual</h1>
        <p class="text-sm text-slate-500 mt-1">Uma mentoria personalizada baseada no seu histórico de redações.</p>
      </div>
      <button (click)="onBack()" class="btn-ghost flex items-center text-sm py-2 px-4">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
        </svg>
        Voltar para o Editor
      </button>
    </div>
  </div>

  <!-- 1. Diagnóstico Consolidado -->
  <div class="bg-white p-5 md:p-6 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-base font-bold tracking-tight text-slate-900 mb-5">1. Diagnóstico Consolidado</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div class="bg-emerald-50 dark:bg-emerald-950/20 border-l-4 border-emerald-500 p-4 rounded-r-lg">
        <h3 class="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-3">Principais Pontos Fortes</h3>
        <div class="space-y-3">
          @for (item of plan().consolidatedDiagnosis; track $index) {
            @if (item.type === 'strength') {
              <div>
                <p class="text-sm font-medium text-emerald-700 dark:text-emerald-400">{{ item.point }}</p>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{{ item.description }} (<i>{{ item.evidence }}</i>)</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"><b>Impacto:</b> {{ item.impact }}</p>
              </div>
            }
          }
        </div>
      </div>
       <div class="bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 p-4 rounded-r-lg">
        <h3 class="text-sm font-semibold text-red-800 dark:text-red-300 mb-3">Principais Gargalos</h3>
        <div class="space-y-3">
          @for (item of plan().consolidatedDiagnosis; track $index) {
            @if (item.type === 'weakness') {
              <div>
                <p class="text-sm font-medium text-red-700 dark:text-red-400">{{ item.point }}</p>
                <p class="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{{ item.description }} (<i>{{ item.evidence }}</i>)</p>
                 <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"><b>Impacto:</b> {{ item.impact }}</p>
              </div>
            }
          }
        </div>
      </div>
    </div>
  </div>

  <!-- 2. Estratégias Práticas de Evolução -->
  <div class="bg-white p-5 md:p-6 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-base font-bold tracking-tight text-slate-900 mb-5">2. Estratégias Práticas de Evolução</h2>
    <div class="space-y-4">
      @for(strategy of plan().evolutionStrategies; track $index) {
        <div class="bg-custom-gray dark:bg-gray-700/30 p-4 rounded-lg">
          <h3 class="text-sm font-semibold text-custom-black dark:text-white">Para melhorar: <span class="text-custom-blue">{{ strategy.weakness }}</span></h3>
          <p class="text-sm text-gray-600 dark:text-gray-300 mt-1.5 mb-3">{{ strategy.strategy }}</p>
          <div class="space-y-1.5">
             @for(step of strategy.steps; track $index) {
              <div class="flex items-start gap-2">
                 <svg class="w-4 h-4 text-custom-blue mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.06 0l4-5.5Z" clip-rule="evenodd" /></svg>
                 <span class="text-xs text-gray-700 dark:text-gray-200">{{ step }}</span>
              </div>
             }
          </div>
        </div>
      }
    </div>
  </div>

  <!-- 3. Exercícios Personalizados -->
  <div class="bg-white p-5 md:p-6 rounded-2xl shadow-card border border-slate-200/80">
    <h2 class="text-base font-bold tracking-tight text-slate-900 mb-5">3. Exercícios Personalizados para Você</h2>
    <div class="space-y-3">
      @for(exercise of plan().personalizedExercises; track $index) {
        <details class="group bg-white dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700 p-4 rounded-lg">
          <summary class="flex justify-between items-center cursor-pointer">
            <div class="flex items-center font-medium text-sm text-custom-black dark:text-white">
              <div class="w-7 h-7 bg-indigo-50 dark:bg-indigo-950/40 text-custom-blue rounded-md flex items-center justify-center mr-2.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="getIconForCategory(exercise.category)"/>
                </svg>
              </div>
              {{ exercise.title }}
            </div>
            <svg class="w-4 h-4 transform group-open:rotate-180 transition-transform text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
          </summary>
          <div class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
            <p class="mb-1.5"><span class="font-medium text-gray-700 dark:text-gray-200">Habilidade-alvo:</span> {{ exercise.category }}</p>
            <p>{{ exercise.description }}</p>
          </div>
        </details>
      }
    </div>
  </div>

</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
})
export class EvolutionPlanComponent {
  plan = input.required<EvolutionPlan>();
  back = output<void>();

  onBack() {
    this.back.emit();
  }

  getIconForCategory(category: string): string {
    const lowerName = category.toLowerCase();
    if (lowerName.includes('organização')) return 'M3.75 9h16.5m-16.5 6.75h16.5';
    if (lowerName.includes('coerência') || lowerName.includes('causa')) return 'M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3';
    if (lowerName.includes('vocabulário')) return 'M10.5 21l-5.25-11.625-5.25 11.625';
    if (lowerName.includes('tese')) return 'M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z';
    if (lowerName.includes('argumenta')) return 'M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z';
    if (lowerName.includes('conclusão')) return 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z';
    return 'M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25';
  }
}